import * as React from 'react';
import dayjs from 'dayjs';
import { MdAssessment, MdCheckCircle, MdError, MdHourglassTop, MdPlayArrow, MdRefresh } from 'react-icons/md';
import { toast } from 'react-toastify';
import DataGrid from '../../Components/Table/DataGrid';
import PageTitle from '../../Components/Common/PageTitle';
import CustomDatePicker from '../../Components/Common/CustomDatePicker';
import ViewEmptyState from '../../Components/Common/ViewEmptyState';
import SearchableDropdown from '../../Components/Subscription/SearchableDropdown';
import { useAuth } from '../../Context/AuthContext';
import { formatUtcToLocalDateTime, toApiDate } from '../../Utils/DateTime';
import { getReportAutomations, getReportLogins, getReportRuns, runReport } from '../../Services/ReportsService';

// The page re-fetches on its own: quickly while a run is queued/running so its result shows up,
// and slowly otherwise so runs triggered by other people appear without a manual refresh.
const ACTIVE_POLL_INTERVAL_MS = 15000;
const IDLE_POLL_INTERVAL_MS = 60000;

// Run statuses are <automation>-QUEUED / -RUNNING / -COMPLETED / -FAILED; the badge keys off the
// suffix (hc-run-status--queued / --running / --completed / --failed in App.css).
const RUN_STATES = ['QUEUED', 'RUNNING', 'COMPLETED', 'FAILED'];

const runState = (run) => (run?.status_code || '').split('-').pop();

function RunStatusBadge({ run }) {
  if (!run) {
    return <span className="text-secondary small text-nowrap">No runs yet</span>;
  }
  const state = runState(run);
  const stateClass = RUN_STATES.includes(state) ? `hc-run-status--${state.toLowerCase()}` : 'hc-run-status--unknown';
  return (
    <span
      className={`badge rounded-pill fw-bolder px-3 py-2 text-center d-inline-flex align-items-center justify-content-center gap-1 text-nowrap hc-run-status ${stateClass}`}
    >
      {run.is_active ? (
        <span className="spinner-grow" style={{ width: '0.5rem', height: '0.5rem' }} aria-hidden="true" />
      ) : null}
      {run.status_name || run.status_code}
    </span>
  );
}

// Same markup as the label SearchableDropdown renders, so the date fields line up with the dropdowns.
function FieldLabel({ children, optional = false }) {
  return (
    <label className="form-label fw-semibold mb-2">
      {children}
      {optional ? (
        <span className="text-secondary fw-normal small ms-1">(optional)</span>
      ) : (
        <span className="text-danger ms-1">*</span>
      )}
    </label>
  );
}

function RunFormSkeleton() {
  return (
    <div className="row g-3 align-items-end">
      {[0, 1, 2, 3].map((index) => (
        <div key={index} className="col-12 col-md-6">
          <div className="skeleton rounded-2 mb-2" style={{ height: 14, width: '40%' }} />
          <div className="skeleton rounded-2" style={{ height: 40 }} />
        </div>
      ))}
      <div className="col-12 d-flex justify-content-end">
        <div className="skeleton rounded-3" style={{ height: 40, width: 160 }} />
      </div>
    </div>
  );
}

function RunHistorySkeleton() {
  return (
    <div>
      {[...Array(5)].map((_, index) => (
        <div key={index} className="d-flex gap-3 align-items-center mb-3">
          <div className="skeleton rounded-2" style={{ height: 16, width: '16%' }} />
          <div className="skeleton rounded-2" style={{ height: 16, width: '10%' }} />
          <div className="skeleton rounded-2" style={{ height: 16, width: '20%' }} />
          <div className="skeleton rounded-pill" style={{ height: 22, width: '10%' }} />
          <div className="skeleton rounded-2" style={{ height: 16, width: '18%' }} />
          <div className="skeleton rounded-2" style={{ height: 16, width: '14%' }} />
        </div>
      ))}
    </div>
  );
}

export default function Reports() {
  const { token } = useAuth();
  const [automations, setAutomations] = React.useState([]);
  const [automation, setAutomation] = React.useState('');
  const [logins, setLogins] = React.useState([]);
  const [loginId, setLoginId] = React.useState('');
  const [fromDate, setFromDate] = React.useState('');
  const [toDate, setToDate] = React.useState('');
  const [runs, setRuns] = React.useState([]);
  const [loading, setLoading] = React.useState(false);
  const [loginsLoading, setLoginsLoading] = React.useState(false);
  const [refreshing, setRefreshing] = React.useState(false);
  const [submitting, setSubmitting] = React.useState(false);
  const [lastUpdated, setLastUpdated] = React.useState(null);

  const loadRuns = React.useCallback(async () => {
    const response = await getReportRuns(token);
    if (response?.is_success) setRuns(response.data || []);
  }, [token]);

  const loadLogins = React.useCallback(async (code, { silent = false } = {}) => {
    if (!code) {
      setLogins([]);
      return;
    }
    try {
      if (!silent) setLoginsLoading(true);
      const response = await getReportLogins(code, token);
      if (response?.is_success) {
        setLogins(response.data || []);
      } else if (!silent) {
        setLogins([]);
        toast.error(response?.message || 'Could not load the logins for this automation.');
      }
    } catch (error) {
      console.error(error);
    } finally {
      if (!silent) setLoginsLoading(false);
    }
  }, [token]);

  // First load: automations (pre-selecting the only one, if there is just one) and run history.
  React.useEffect(() => {
    if (!token) return;
    (async () => {
      try {
        setLoading(true);
        const [automationsResponse] = await Promise.all([getReportAutomations(token), loadRuns()]);
        if (automationsResponse?.is_success) {
          const list = automationsResponse.data || [];
          setAutomations(list);
          if (list.length === 1) setAutomation(list[0].code);
        } else if (automationsResponse?.status_code === 500) {
          toast.error('Internal Server Error. Please Contact Support Team.');
        }
        setLastUpdated(new Date().toISOString());
      } catch (error) {
        console.error(error);
      } finally {
        setLoading(false);
      }
    })();
  }, [token, loadRuns]);

  React.useEffect(() => {
    setLoginId('');
    loadLogins(automation);
  }, [automation, loadLogins]);

  // silent: background poll, no loaders; otherwise the Refresh button spins.
  const refresh = React.useCallback(async ({ silent = false } = {}) => {
    try {
      if (!silent) setRefreshing(true);
      await Promise.all([loadRuns(), loadLogins(automation, { silent: true })]);
      setLastUpdated(new Date().toISOString());
    } catch (error) {
      console.error(error);
    } finally {
      if (!silent) setRefreshing(false);
    }
  }, [loadRuns, loadLogins, automation]);

  const hasActiveRun = React.useMemo(() => runs.some((run) => run.is_active), [runs]);

  React.useEffect(() => {
    if (!token) return undefined;
    const timer = setInterval(
      () => refresh({ silent: true }),
      hasActiveRun ? ACTIVE_POLL_INTERVAL_MS : IDLE_POLL_INTERVAL_MS,
    );
    return () => clearInterval(timer);
  }, [token, hasActiveRun, refresh]);

  const selectedAutomation = automations.find((item) => item.code === automation);
  const selectedLogin = logins.find((login) => login.id === loginId);
  const lastRun = selectedLogin?.last_run;
  const loginBusy = Boolean(lastRun?.is_active);
  const datesInvalid = Boolean(fromDate && toDate && fromDate > toDate);
  // From/To are optional: a blank end uses the automation's default range.
  const canRun = Boolean(automation && selectedLogin && !datesInvalid && !loginBusy && !submitting);

  const handleRun = async () => {
    if (!canRun) return;
    try {
      setSubmitting(true);
      const response = await runReport(
        { automation, login_cred_id: selectedLogin.id, from_date: toApiDate(fromDate) || null, to_date: toApiDate(toDate) || null },
        token,
      );
      if (response?.is_success) {
        toast.success(
          `${selectedAutomation?.name} for ${selectedLogin.company_name} started. It will be emailed to ${response.data?.recipient_email} when ready.`,
        );
      } else if (response?.status_code === 500) {
        toast.error('Internal Server Error. Please Contact Support Team.');
      } else {
        toast.error(response?.message || 'Could not start the report. Please try again.');
      }
      // Re-fetch either way: on a 409 someone else already started this login, so show their run.
      await refresh({ silent: true });
    } catch (error) {
      console.error(error);
      toast.error('Something went wrong while starting the report.');
    } finally {
      setSubmitting(false);
    }
  };

  const stats = React.useMemo(() => {
    const count = (state) => runs.filter((run) => runState(run) === state).length;
    return [
      {
        label: 'Total Runs',
        value: runs.length,
        icon: <MdAssessment size={22} color="var(--hc-icon-blue-text)" />,
        iconClass: 'hc-kpi-icon--blue',
        accentClass: 'border-primary',
        valueClass: 'text-primary',
      },
      {
        label: 'In Progress',
        value: runs.filter((run) => run.is_active).length,
        icon: <MdHourglassTop size={22} color="var(--hc-status-warning-text)" />,
        iconClass: 'hc-kpi-icon--amber',
        accentClass: 'border-warning',
        valueClass: 'text-warning',
      },
      {
        label: 'Completed',
        value: count('COMPLETED'),
        icon: <MdCheckCircle size={22} color="var(--hc-status-success-text)" />,
        iconClass: 'hc-kpi-icon--green',
        accentClass: 'border-success',
        valueClass: 'text-success',
      },
      {
        label: 'Failed',
        value: count('FAILED'),
        icon: <MdError size={22} color="var(--hc-status-danger-text)" />,
        iconClass: 'hc-kpi-icon--danger',
        accentClass: 'border-danger',
        valueClass: 'text-danger',
      },
    ];
  }, [runs]);

  const loginOptions = logins.map((login) => ({
    value: login.id,
    label: [login.company_name, login.office_id ? `Office ${login.office_id}` : null, login.organization_name]
      .filter(Boolean)
      .join(' · '),
  }));

  const runColumns = React.useMemo(() => [
    { id: 'automation_name', label: 'Automation', minWidth: 170 },
    { id: 'company_name', label: 'Login', minWidth: 90 },
    {
      id: 'from_date',
      label: 'Period',
      minWidth: 190,
      format: (value, row) => row.report_period || '—',
    },
    {
      id: 'status_name',
      label: 'Status',
      minWidth: 130,
      align: 'center',
      format: (value, row) => <RunStatusBadge run={row} />,
    },
    { id: 'triggered_by', label: 'Triggered By', minWidth: 140, format: (value) => value || '—' },
    { id: 'triggered_on', label: 'Triggered On', minWidth: 170, format: (value) => formatUtcToLocalDateTime(value) },
    { id: 'finished_at', label: 'Finished', minWidth: 170, format: (value) => formatUtcToLocalDateTime(value) },
    { id: 'recipient_email', label: 'Emailed To', minWidth: 180 },
    {
      id: 'error',
      label: 'Error',
      minWidth: 220,
      format: (value) => (value ? <span className="text-danger small text-break">{value}</span> : '—'),
    },
  ], []);

  return (
    // minmax(0, 1fr) keeps the page at the available width: the wide run-history table scrolls
    // inside its own container instead of stretching the whole layout past the screen.
    <div className="d-grid" style={{ gridTemplateColumns: 'minmax(0, 1fr)' }}>
      <PageTitle title="Reports - Aegis 360" />
      <div className="d-flex flex-md-row flex-column align-items-md-center justify-content-between gap-2 mb-4">
        <div style={{ minWidth: 0 }}>
          <h4 className="fw-bold mb-1 hc-text-primary fs-3">Reports</h4>
          <p className="text-secondary mb-0 small">
            Run an automation for a login and period. The report is emailed to the login's recipients when it finishes.
          </p>
        </div>
        <div className="d-flex flex-md-column align-items-center align-items-md-end gap-2 flex-shrink-0">
          <button
            type="button"
            onClick={() => refresh()}
            disabled={refreshing || loading}
            title="Reload the logins and run history now"
            className="btn btn-outline-secondary btn-sm fw-semibold d-flex align-items-center justify-content-center gap-1 px-3 py-2 rounded-3 text-nowrap"
          >
            {refreshing ? <span className="spinner-border spinner-border-sm" aria-hidden="true" /> : <MdRefresh size={18} />}
            <span>Refresh</span>
          </button>
          {lastUpdated ? (
            <span className="text-secondary text-nowrap" style={{ fontSize: '0.75rem' }}>
              Updated {formatUtcToLocalDateTime(lastUpdated)}
            </span>
          ) : null}
        </div>
      </div>

      <div className="row g-3 mb-4">
        {stats.map((stat) => (
          <div key={stat.label} className="col-12 col-sm-6 col-xl-3">
            {loading ? (
              <div className="bg-white p-4 rounded-4 shadow">
                <div className="skeleton mb-2" style={{ height: 16, width: '60%' }} />
                <div className="skeleton position-relative overflow-hidden rounded-2" style={{ height: 28, width: '40%' }} />
              </div>
            ) : (
              <div className="position-relative overflow-hidden bg-white p-4 rounded-4 shadow p-3 d-flex align-items-center gap-3 h-100 hc-list-kpi-card">
                <div className={`position-absolute start-0 top-0 h-100 border-start border-5 ${stat.accentClass}`} />
                <div
                  className={`d-inline-flex align-items-center justify-content-center rounded-3 ${stat.iconClass}`}
                  style={{ width: 44, height: 44 }}
                >
                  {stat.icon}
                </div>
                <div className="d-flex flex-column fw-bold lh-sm">
                  <span className="text-dark" style={{ fontSize: '0.85rem' }}>{stat.label}</span>
                  <span className={`fw-bold ${stat.valueClass} fs-5`}>{stat.value}</span>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>

      {!loading && automations.length === 0 ? (
        <ViewEmptyState
          containerClassName="mb-4"
          icon={MdAssessment}
          title="No report automations available"
          description="There are no automations that can be run from this page yet."
        />
      ) : (
        <div className="bg-white rounded-3 shadow-lg p-3 p-md-4 mb-4">
          <h6 className="fw-bold hc-text-primary mb-3">Run a report</h6>
          {loading ? (
            <RunFormSkeleton />
          ) : (
            <>
              <div className="row g-3 align-items-end">
                <div className="col-12 col-md-6">
                  <SearchableDropdown
                    label="Automation"
                    placeholder="Select automation"
                    options={automations.map((item) => ({ value: item.code, label: item.name }))}
                    value={automation}
                    onChange={(value) => setAutomation(value || '')}
                    showSearch={automations.length > 5}
                    required
                  />
                </div>
                <div className="col-12 col-md-6">
                  <SearchableDropdown
                    label="Login"
                    placeholder={automation ? 'Select login' : 'Select an automation first'}
                    options={loginOptions}
                    value={loginId}
                    onChange={(value) => setLoginId(value || '')}
                    disabled={!automation || loginsLoading}
                    loading={loginsLoading}
                    emptyMessage="No logins are set up for this automation"
                    showSearch={loginOptions.length > 5}
                    required
                  />
                </div>
                <div className="col-12 col-md-6">
                  <FieldLabel optional>From</FieldLabel>
                  <CustomDatePicker
                    value={fromDate}
                    onChange={setFromDate}
                    maxDate={toDate ? dayjs(toDate) : undefined}
                  />
                </div>
                <div className="col-12 col-md-6">
                  <FieldLabel optional>To</FieldLabel>
                  <CustomDatePicker
                    value={toDate}
                    onChange={setToDate}
                    minDate={fromDate ? dayjs(fromDate) : undefined}
                  />
                </div>
                <div className="col-12 d-flex justify-content-end">
                  <button
                    type="button"
                    disabled={!canRun}
                    onClick={handleRun}
                    className="btn hc-bg-primary btn-sm text-white fw-semibold d-flex align-items-center justify-content-center gap-2 px-4 rounded-3 text-nowrap"
                    style={{ height: 40, minWidth: 160 }}
                  >
                    {submitting ? <span className="spinner-border spinner-border-sm" aria-hidden="true" /> : <MdPlayArrow size={18} />}
                    <span>{loginBusy ? 'In progress' : submitting ? 'Starting…' : 'Run report'}</span>
                  </button>
                </div>
              </div>

              <div className="d-flex flex-wrap align-items-center gap-2 mt-2 small">
                {datesInvalid ? (
                  <span className="text-danger">From date must be on or before the To date.</span>
                ) : selectedAutomation?.period_hint ? (
                  <span className="text-secondary">{selectedAutomation.period_hint}</span>
                ) : null}
                {fromDate || toDate ? (
                  <button
                    type="button"
                    className="btn btn-link btn-sm p-0 small text-decoration-none"
                    onClick={() => {
                      setFromDate('');
                      setToDate('');
                    }}
                  >
                    Clear dates
                  </button>
                ) : null}
              </div>

              {selectedAutomation?.description ? (
                <div className="small text-secondary mt-3">{selectedAutomation.description}</div>
              ) : null}

              {selectedLogin ? (
                <div className="border rounded-3 p-3 mt-3 d-flex flex-column flex-lg-row gap-3 justify-content-between">
                  <div className="small text-break" style={{ minWidth: 0 }}>
                    <div><span className="text-secondary">Emails to:</span> {selectedLogin.to_email || '—'}</div>
                    {selectedLogin.cc_emails?.length ? (
                      <div><span className="text-secondary">CC:</span> {selectedLogin.cc_emails.join(', ')}</div>
                    ) : null}
                  </div>
                  <div className="small text-lg-end text-break" style={{ minWidth: 0 }}>
                    <div className="mb-1"><RunStatusBadge run={lastRun} /></div>
                    {lastRun ? (
                      <div className="text-secondary">
                        Last run {lastRun.report_period}, {formatUtcToLocalDateTime(lastRun.triggered_on)}
                        {lastRun.triggered_by ? ` by ${lastRun.triggered_by}` : ''}
                      </div>
                    ) : null}
                    {runState(lastRun) === 'FAILED' && lastRun.error ? (
                      <div className="text-danger mt-1">{lastRun.error}</div>
                    ) : null}
                  </div>
                </div>
              ) : null}
            </>
          )}
        </div>
      )}

      <div className="bg-white rounded-3 shadow-lg p-3 p-md-4">
        <h6 className="fw-bold hc-text-primary mb-3">Run history</h6>
        <div className="bg-white p-3 rounded shadow">
          {loading ? (
            <RunHistorySkeleton />
          ) : (
            <DataGrid
              columns={runColumns}
              rows={runs}
              defaultRowsPerPage={10}
              stickyHeader
              getRowId={(row) => row.id}
            />
          )}
        </div>
      </div>
    </div>
  );
}
